import { SearchBar } from '@common/components/SearchBar';
import { Title } from '@common/components/Title';
import {
    UseFilterStateResult,
    UsePaginationResult,
    UseSearchResult,
    UseSortingResult,
} from '@common/hooks';
import { Action } from '@common/types/actions';
import { NavigationTabsOption } from '@common/types/link';
import { APIFacetField } from '@common/types/queryState';
import { mergeWithDefault } from '@common/utils/mergeWithDefault';
import { FeatureConfiguration } from '@features/tableEngine/types';
import { Box, Card, CardProps, Flex, Grid } from '@radix-ui/themes';
import { ColumnDef } from '@tanstack/react-table';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelection, useTable, UseTableProps } from '../hooks';
import { createSelectionColumn } from './createSelectionColumn';
import { SelectionRow } from './SelectionRow';
import { TableColumnControlPanel } from './TableColumnControlPanel';
import { TableDisplay } from './TableDisplay';
import { TablePaginationControl } from './TablePaginationControl';
import { TableTabControl } from './TableTabControl';

export interface TableCardProps<
    T extends { id: string },
    TTab extends string = string,
> {
    data: T[];
    itemCount: number;
    pageCount: number;

    searchState: UseSearchResult;
    paginationState: UsePaginationResult;
    sortingState: UseSortingResult;

    facet?: APIFacetField[];
    filterState?: UseFilterStateResult;
    renderFacets?: (
        data: APIFacetField[],
        state: UseFilterStateResult,
    ) => React.ReactNode;

    tabOptions?: NavigationTabsOption<TTab>[];
    config: Partial<FeatureConfiguration<T>>;

    actions?: Action<T>[];

    isLoading?: boolean;
    isFetching?: boolean;
    maxRows?: number | 'unlimited';
    minRows?: number;
    size?: CardProps['size'];
    toolbarControls?: React.ReactNode;
}

const PAGE_SIZES = [...Array(5)].map((_, i) => (i + 1) * 10);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const DEFAULT_FEATURE_CONFIGURATION: FeatureConfiguration<any> = {
    search: { enabled: true },
    pagination: { enabled: true, pageSizes: PAGE_SIZES },
    sorting: { enabled: true },
    selection: { enabled: true, displayKey: 'id' },
    facets: { enabled: false },
    modes: { enabled: false },
    columnControl: { enabled: true },
    columnResizing: { enabled: true },
    columns: [],
};

export const TableCard = <
    TData extends { id: string },
    TTab extends string = string,
>({
    data,
    itemCount,
    pageCount,
    searchState,
    paginationState,
    sortingState,
    facet: facetData,
    filterState,
    renderFacets,
    tabOptions,
    config,
    actions = [],
    isLoading,
    isFetching,
    maxRows = 10,
    minRows = 2,
    size = '3',
    toolbarControls,
}: TableCardProps<TData, TTab>) => {
    const { t } = useTranslation('table');
    const selectionState = useSelection<TData>();

    const mergedConfig = mergeWithDefault(
        config,
        DEFAULT_FEATURE_CONFIGURATION,
    ) as FeatureConfiguration<TData>;

    const {
        title,
        columns,
        search,
        pagination,
        sorting,
        selection,
        facets,
        columnControl,
        columnResizing,
    } = mergedConfig;

    const finalColumns: ColumnDef<TData>[] = useMemo(() => {
        const result: ColumnDef<TData>[] = [];
        if (selection.enabled) result.push(createSelectionColumn<TData>());
        result.push(...columns);
        return result;
    }, [columns, selection.enabled]);

    const smartTableArgs: UseTableProps<TData> = {
        data,
        columns: finalColumns,
        rowCount: itemCount,
        pageCount,
    };

    if (search.enabled) smartTableArgs.searchState = searchState;
    if (pagination.enabled) smartTableArgs.paginationState = paginationState;
    if (sorting.enabled) smartTableArgs.sortingState = sortingState;
    if (selection.enabled) smartTableArgs.selectionState = selectionState;

    const { table } = useTable(smartTableArgs);

    React.useEffect(() => {
        if (columnControl.enabled && columnControl.columnOrder) {
            table.setColumnOrder(columnControl.columnOrder);
        }
    }, [columnControl, table]);

    const ROW_HEIGHT = 46;
    const isUnlimitedHeight = maxRows === 'unlimited';
    const numericalMaxRows = typeof maxRows === 'number' ? maxRows : 10;

    const tableViewportHeight = (numericalMaxRows + 1) * ROW_HEIGHT + 1;
    const minTableViewportHeight = (minRows + 1) * ROW_HEIGHT + 1;

    const showFacets = facets.enabled && !!facetData && !!filterState;
    const gridColumns = showFacets
        ? { initial: '1fr', md: '280px 1fr' }
        : '1fr';

    return (
        <Card
            variant={'surface'}
            size={size}
            style={{
                height: '100%',
                maxHeight: '100%',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                padding: 0,
            }}
        >
            <Grid columns={gridColumns} style={{ flexGrow: 1, minHeight: 0 }}>
                {showFacets && (
                    <Box
                        display={{ initial: 'none', md: 'block' }}
                        style={{
                            position: 'relative',
                            borderRight: '1px solid var(--gray-a5)',
                        }}
                    >
                        <Flex
                            direction='column'
                            style={{
                                position: 'absolute',
                                inset: 0,
                                minHeight: 0,
                            }}
                        >
                            {renderFacets?.(facetData, filterState)}
                        </Flex>
                    </Box>
                )}

                <Flex
                    direction={'column'}
                    gap={'2'}
                    p={size}
                    m={size}
                    style={{ minWidth: 0, minHeight: 0, height: '100%' }}
                >
                    <Flex flexShrink={'0'} direction={'column'} gap={'2'}>
                        {title && <Title>{title}</Title>}

                        <Flex justify={'between'}>
                            <Box width={{ initial: '100%', sm: '682px' }}>
                                {search.enabled && (
                                    <SearchBar
                                        name='search'
                                        size={'3'}
                                        placeholder={
                                            search.placeholder ??
                                            t('search.placeholder')
                                        }
                                        {...searchState}
                                    />
                                )}
                            </Box>
                            {toolbarControls}
                        </Flex>

                        <Flex justify={'between'} align={'center'}>
                            <Box>
                                {selection.enabled && (
                                    <SelectionRow
                                        table={table}
                                        selectionState={selectionState}
                                        selection={selection}
                                        actions={actions}
                                    />
                                )}
                            </Box>

                            <Flex gap={'3'} align={'center'}>
                                {tabOptions && (
                                    <TableTabControl options={tabOptions} />
                                )}
                                {columnControl.enabled && (
                                    <TableColumnControlPanel table={table} />
                                )}
                            </Flex>
                        </Flex>
                    </Flex>

                    <Box
                        style={{
                            flexGrow: 1,
                            flexShrink: 1,
                            minHeight: `${minTableViewportHeight}px`,
                            maxHeight: isUnlimitedHeight
                                ? 'none'
                                : `${tableViewportHeight}px`,
                            position: 'relative',
                        }}
                    >
                        <TableDisplay
                            table={table}
                            isSortingEnabled={sorting.enabled}
                            isResizingEnabled={columnResizing.enabled}
                            isLoading={isLoading || isFetching}
                        />
                    </Box>

                    {pagination.enabled && pagination.pageSizes && (
                        <Flex
                            align={'stretch'}
                            justify={'end'}
                            flexShrink={'0'}
                            pt={'2'}
                        >
                            <TablePaginationControl
                                table={table}
                                pageSizes={pagination.pageSizes}
                                isPaginated={true}
                            />
                        </Flex>
                    )}
                </Flex>
            </Grid>
        </Card>
    );
};
